/**
 * 管理员管理页面
 */
import React, { useRef, useState } from "react";

// 通用组件
import {
  GenericCardsStatistic,
  GenericModalTree,
  ContainerPanel,
  ContainerModal,
  CompsTable,
  CompsSearch,
  CompsForm,
} from "@ms/shared-components";
// Ant Design 组件和图标在 hooks 和 config 中使用，这里不需要导入

// 本地组件和配置
import { useAdminData } from "./hooks/useAdminData";
import { useAdminOperations } from "./hooks/useAdminOperations";
import { useAdminModals } from "./hooks/useAdminModals";
import { useAdminStatistics } from "./hooks/useAdminStatistics";
import { usePermissionTreeData } from "./hooks/usePermissionTreeData";
import { useAdminHandlers } from "./hooks/useAdminHandlers";
import { useAdminSearchAndPagination } from "./hooks/useAdminSearchAndPagination";
import { useAdminInitialization } from "./hooks/useAdminInitialization";
import {
  AdminRecord,
  adminSearchConfig,
  getAdminTableColumns,
  getAdminTableButtons,
  tableConfig,
} from "./config/panelTableConfig";
import { generateAdminStatistics, statisticConfig } from "./config/statisticConfig";
import { createAdminFormConfig, editAdminFormConfig, createEditModalConfig } from "./config/modalFormConfig";
import { roleTableColumns, assignRoleModalConfig, selectionConfig } from "./config/modalRolesConfig";
import { permissionModalConfig } from "./config/modalPermissionConfig";
import { searchConfig, getSearchPanelButtons } from "./config/panelSearchConfig";
import { generateModalTitle, handleRoleSelectionChange } from "./utils/assignRoleUtils";

const AdminsPage: React.FC = () => {
  // 基础状态管理
  const [searchParams, setSearchParams] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // 弹窗状态管理
  const {
    createModalVisible,
    editModalVisible,
    assignRoleModalVisible,
    permissionModalVisible,
    currentRecord,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openAssignRoleModal,
    closeAssignRoleModal,
    openPermissionModal,
    closePermissionModal,
  } = useAdminModals();

  // 数据管理
  const {
    adminList,
    adminLoading,
    adminTotal,
    fetchAdminList,
    permissions,
    permissionLoading,
    fetchPermissions,
    permissionCounts,
    permissionCountLoading,
    loadPermissionCounts,
    updatePermissionCount,
    allRoles,
    selectedRoles,
    roleLoading,
    fetchAdminRoles,
    setSelectedRoles,
  } = useAdminData();

  // 操作管理
  const {
    submitting,
    roleSubmitting,
    createAdmin,
    updateAdmin,
    updateAdminStatus,
    resetPassword,
    deleteAdmin,
    batchDeleteAdmins,
    assignRolesToAdmin,
  } = useAdminOperations();

  // 统计数据处理
  const { adminStats, loadAdminStats } = useAdminStatistics({ adminList });

  // 权限树数据转换
  const { permissionTreeData } = usePermissionTreeData({
    permissions,
    currentRecord,
  });

  // 搜索和分页处理
  const { handleSearch, handleReset, handlePaginationChange } = useAdminSearchAndPagination({
    searchParams,
    setSearchParams,
    pagination,
    setPagination,
    fetchAdminList,
  });

  // 分配角色时需要同时获取角色列表
  const handleAssignRole = async (record: AdminRecord) => {
    openAssignRoleModal(record);
    fetchAdminRoles(record.id);
  };

  // 业务处理函数
  const {
    handleCreate,
    handleEdit,
    handleToggleStatus,
    handleDelete,
    handleResetPassword,
    handleRoleSave,
    handleViewPermissions,
    handleBatchDelete,
  } = useAdminHandlers({
    createAdmin,
    updateAdmin,
    updateAdminStatus,
    resetPassword,
    deleteAdmin,
    batchDeleteAdmins,
    assignRolesToAdmin,
    fetchPermissions,
    closeCreateModal,
    closeEditModal,
    closeAssignRoleModal,
    openPermissionModal,
    openAssignRoleModal,
    openEditModal,
    handleSearch,
    loadAdminStats,
    updatePermissionCount,
    currentRecord,
    selectedRowKeys,
    setSelectedRowKeys,
  });

  // 使用配置中的按钮设置
  const tableButtons = getAdminTableButtons(
    () => handleSearch(), // onRefresh
    openCreateModal, // onCreate
    handleBatchDelete, // onBatchDelete
    selectedRowKeys.length // selectedCount
  );

  // 初始化逻辑
  useAdminInitialization({
    handleSearch,
    adminList,
    loadPermissionCounts,
  });

  return (
    <div
      className="role-management-container"
      style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, gap: 16 }}
    >
      {/* 第一行：统计信息 */}
      <GenericCardsStatistic
        data={generateAdminStatistics(adminStats).map((card, index) => ({
          key: `stat-card-${index}`,
          title: card.title,
          value: card.value,
          color: card.valueStyle?.color,
        }))}
        columns={statisticConfig.columns}
        gutter={statisticConfig.gutter}
      />

      {/* 第二行：搜索栏（ContainerPanel + CompsSearch） */}
      {(() => {
        const searchFormRef = useRef<any>(null);
        return (
          <ContainerPanel
            title={searchConfig.title}
            height="auto"
            buttons={getSearchPanelButtons(
              handleReset,
              (values?: any) => handleSearch(values),
              () => (searchFormRef.current ? searchFormRef.current.getFieldsValue() : undefined)
            )}
          >
            <CompsSearch
              searchItems={adminSearchConfig.fields as any[]}
              columns={searchConfig.columns}
              onSearch={handleSearch}
              onReset={handleReset}
              collapsible={searchConfig.collapsible}
              defaultCollapsed={searchConfig.defaultCollapsed}
              formRef={(form) => (searchFormRef.current = form)}
            />
          </ContainerPanel>
        );
      })()}

      {/* 第三行：表格（ContainerPanel + CompsTable） */}
      <ContainerPanel
        title={tableConfig.title}
        buttons={tableButtons as any}
        height="100%"
        cardProps={{ style: { flex: 1, minHeight: 0 } }}
      >
        <CompsTable
          tableProps={{
            columns: getAdminTableColumns(
              handleToggleStatus,
              handleAssignRole,
              openEditModal,
              handleDelete,
              handleResetPassword,
              handleViewPermissions
            ) as any,
            dataSource: adminList.map((admin) => ({
              ...admin,
              permission_count: permissionCounts[admin.id] || 0,
            })),
            rowKey: "id",
            scroll: tableConfig.scroll,
            rowSelection: {
              selectedRowKeys,
              onChange: (keys: React.Key[]): void => {
                setSelectedRowKeys(keys as string[]);
              },
            },
          }}
          loading={adminLoading}
          showSelection={tableConfig.showSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: adminTotal,
            ...(tableConfig.pagination as any),
          }}
          onPaginationChange={handlePaginationChange as any}
          showColumnSetting={tableConfig.showColumnSetting}
          storageKey={tableConfig.storageKey}
          resizableColumns={tableConfig.resizableColumns}
          saveColumnWidths={tableConfig.saveColumnWidths}
        />
      </ContainerPanel>

      {/* 创建管理员弹窗 */}
      {(() => {
        const createFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title="创建管理员"
            visible={createModalVisible}
            width={createEditModalConfig.width}
            height={createEditModalConfig.height}
            onCancel={closeCreateModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: createEditModalConfig.cancelText,
                type: "default",
                onClick: closeCreateModal,
              },
              {
                key: "ok",
                text: createEditModalConfig.okText,
                type: "primary",
                onClick: (): void => createFormRef.current?.submit(),
              },
            ]}
            modalProps={{
              confirmLoading: submitting,
              ...(createEditModalConfig.modalProps as any),
            }}
          >
            <CompsForm
              formItems={createAdminFormConfig as any}
              formRef={(form) => (createFormRef.current = form)}
              formProps={{
                onFinish: async (values: any) => {
                  const ok = await handleCreate(values);
                  if (ok) {
                    closeCreateModal();
                  }
                },
              }}
              layout={createEditModalConfig.layout as any}
              labelCol={createEditModalConfig.labelCol as any}
              wrapperCol={createEditModalConfig.wrapperCol as any}
            />
          </ContainerModal>
        );
      })()}

      {/* 编辑管理员弹窗 */}
      {(() => {
        const editFormRef = useRef<any>(null);
        return (
          <ContainerModal
            title="编辑管理员"
            visible={editModalVisible}
            width={createEditModalConfig.width}
            height={createEditModalConfig.height}
            onCancel={closeEditModal}
            footerRightButtons={[
              {
                key: "cancel",
                text: createEditModalConfig.cancelText,
                type: "default",
                onClick: closeEditModal,
              },
              {
                key: "ok",
                text: createEditModalConfig.okText,
                type: "primary",
                onClick: (): void => editFormRef.current?.submit(),
              },
            ]}
            modalProps={{
              confirmLoading: submitting,
              ...(createEditModalConfig.modalProps as any),
            }}
          >
            <CompsForm
              formItems={editAdminFormConfig as any}
              initialValues={currentRecord || undefined}
              formRef={(form) => (editFormRef.current = form)}
              formProps={{
                onFinish: async (values: any) => {
                  const ok = await handleEdit(values);
                  if (ok) {
                    closeEditModal();
                  }
                },
              }}
              layout={createEditModalConfig.layout as any}
              labelCol={createEditModalConfig.labelCol as any}
              wrapperCol={createEditModalConfig.wrapperCol as any}
            />
          </ContainerModal>
        );
      })()}

      {/* 分配角色弹窗 */}
      <ContainerModal
        title={generateModalTitle(currentRecord?.admin_account || "")}
        visible={assignRoleModalVisible}
        width={assignRoleModalConfig.width as any}
        onCancel={closeAssignRoleModal}
        footerRightButtons={[
          {
            key: "cancel",
            text: "取消",
            type: "default",
            onClick: closeAssignRoleModal,
          },
          {
            key: "save",
            text: assignRoleModalConfig.saveButtonText || "保存",
            type: "primary",
            loading: roleSubmitting,
            onClick: async (): Promise<void> => {
              await handleRoleSave(selectedRoles as any);
            },
          },
        ]}
        modalProps={assignRoleModalConfig.modalProps as any}
      >
        <CompsTable
          tableProps={{
            columns: roleTableColumns as any,
            dataSource: allRoles as any[],
            loading: roleLoading,
            rowKey: "id",
            scroll: assignRoleModalConfig.scroll as any,
            size: assignRoleModalConfig.size as any,
            rowSelection: {
              type: selectionConfig.type as any,
              selectedRowKeys: selectedRoles.map((key) => key as React.Key),
              onChange: (keys, rows): void => handleRoleSelectionChange(keys, rows as any, setSelectedRoles),
            },
          }}
          showSelection={true}
        />
      </ContainerModal>

      {/* 权限详情弹窗 */}
      <GenericModalTree
        visible={permissionModalVisible}
        title={`${currentRecord?.admin_account} 的权限详情`}
        size={permissionModalConfig.size}
        onClose={closePermissionModal}
        treeData={permissionTreeData}
        searchConfig={permissionModalConfig.searchConfig}
        selectionConfig={permissionModalConfig.selectionConfig}
        actionConfig={permissionModalConfig.actionConfig}
        treeConfig={{
          ...permissionModalConfig.treeConfig,
          loading: permissionLoading,
        }}
        modalProps={permissionModalConfig.modalProps}
      />
    </div>
  );
};

export default AdminsPage;
